import React from "react";
import { Button, Space, Tooltip, Typography } from "antd";
import * as formats from "../../../utils/FormatingFunctions";
import { deriveBookingMeta } from "../lib/bookingMeta";
import { StatusTag, PaymentTag, TicketStateTag } from "./StatusTags";

const { Text } = Typography;

const formatAmount = (amount, currency) => {
  if (amount === undefined || amount === null || !currency) return "—";
  try {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
};

const getRouteSummary = (booking) => {
  const offer = booking?.booking_reference?.data?.flightOffers?.[0];
  const itineraries = offer?.itineraries ?? [];
  if (!itineraries.length) return { label: "—", extraLegs: 0 };

  const firstSegment = itineraries[0]?.segments?.[0];
  const lastItinerary = itineraries[itineraries.length - 1];
  const lastSegment = lastItinerary?.segments?.[lastItinerary.segments.length - 1];

  const totalSegments = itineraries.reduce(
    (sum, itinerary) => sum + (itinerary.segments?.length ?? 0),
    0,
  );

  if (!firstSegment || !lastSegment) return { label: "—", extraLegs: 0 };

  const from = firstSegment.departure?.iataCode;
  const to = lastSegment.arrival?.iataCode;

  return {
    label: `${formats.getAirportCity(from) || from} → ${
      formats.getAirportCity(to) || to
    }`,
    extraLegs: Math.max(totalSegments - 1, 0),
  };
};

const getTravelDate = (booking) => {
  const offer = booking?.booking_reference?.data?.flightOffers?.[0];
  const firstDeparture =
    offer?.itineraries?.[0]?.segments?.[0]?.departure?.at;
  if (!firstDeparture) return null;
  return new Date(firstDeparture);
};

export const makeColumns = ({ canCancel, onView, onIssue, onCancel }) => [
  {
    title: "Booking ref",
    dataIndex: "tramangoId",
    key: "tramangoId",
    render: (_, record) => (
      <div>
        <div>
          <Text strong>{record.tramangoId}</Text>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formats.formatDateInfo(record.createdAt)}
          </Text>
        </div>
      </div>
    ),
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    render: (type) => (type === "FLIGHT-RESERVATION" ? "Reservation" : "Flight"),
  },
  {
    title: "Customer",
    key: "customer",
    render: (_, record) => (
      <div>
        <div>
          {record.firstName} {record.lastName}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.email}
          </Text>
        </div>
      </div>
    ),
  },
  {
    title: "Route",
    key: "route",
    render: (_, record) => {
      const { label, extraLegs } = getRouteSummary(record);
      if (!extraLegs) return label;
      return (
        <Tooltip title={`${extraLegs} additional leg${extraLegs > 1 ? "s" : ""}`}>
          <span>
            {label} <Text type="secondary">+{extraLegs} legs</Text>
          </span>
        </Tooltip>
      );
    },
  },
  {
    title: "Travel date",
    key: "travelDate",
    render: (_, record) => {
      const date = getTravelDate(record);
      if (!date) return "—";
      const isPast = date < new Date();
      return (
        <Text type={isPast ? "danger" : undefined}>
          {formats.formatDateInfo(date)}
        </Text>
      );
    },
  },
  {
    title: "Provider",
    key: "provider",
    render: (_, record) => {
      const meta = deriveBookingMeta(record);
      if (!meta.provider) return "—";
      return (
        <span>
          {meta.provider}
          {meta.providerOffice ? ` (${meta.providerOffice})` : ""}
        </span>
      );
    },
  },
  {
    title: "PNR / Ticket state",
    key: "ticketState",
    render: (_, record) => {
      const meta = deriveBookingMeta(record);
      return (
        <div>
          <TicketStateTag
            ticketed={meta.ticketed}
            hasProviderRef={meta.hasProviderRef}
          />
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {meta.pnr ?? "—"}
            </Text>
          </div>
        </div>
      );
    },
  },
  {
    title: "Amount",
    key: "amount",
    render: (_, record) => formatAmount(record.amount, record.currency),
  },
  {
    title: "Payment",
    key: "payment",
    render: (_, record) => <PaymentTag booking={record} />,
  },
  {
    title: "Status",
    key: "status",
    render: (_, record) => <StatusTag booking={record} />,
  },
  {
    title: "Actions",
    key: "actions",
    render: (_, record) => {
      const meta = deriveBookingMeta(record);
      const showIssue = meta.isFlight && !meta.ticketed && !meta.pnr;
      const isCancelled =
        record.status === "CANCELLED" || record.status === "REFUNDED";
      const cancellationState = record?.cancellation?.state;
      const isRetriable =
        cancellationState === "FAILED" ||
        cancellationState === "PENDING_PROVIDER";

      return (
        <Space size="middle">
          <Button onClick={() => onView(record)}>View</Button>
          {showIssue && (
            <Button onClick={() => onIssue(record)}>Issue ticket</Button>
          )}
          {canCancel && !isCancelled && (
            <Button danger onClick={() => onCancel(record)}>
              {isRetriable ? "Retry cancel" : "Cancel"}
            </Button>
          )}
        </Space>
      );
    },
  },
];
